import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { PermissionsModule } from '@sbd-doug-frontend/app/shared/permissions/permissions.module';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  CIPXActionBarComponent,
  CIPXButtonComponent,
  CIPXCancelButtonComponent,
  CIPXPrimaryButtonComponent,
} from '@sbd-doug-frontend/shared/lib/ui';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { SharedModule } from '@sbd-doug-frontend/app/shared/shared.module';
import { MaterialModule } from '@sbd-doug-frontend/app/shared/material.module';
import { LoadingStateComponent } from '@sbd-doug-frontend/shared/lib/ui/loading-state/loading-state.component';
import { ProcessMissingScheduleResponse, ProcessMissingService, ProcessMissingUiFieldsResponse } from './process-missing.service';
import { CathyService } from '@sbd-doug-frontend/app/services/cathy.service';
import { TitleService } from '@sbd-doug-frontend/app/services/title.service';

type FieldType = 'integer' | 'float' | 'string' | 'text' | 'boolean' | 'datetime' | 'date' | 'timestamp' | 'json';
type FieldMeta = { key: string; type: FieldType; label: string };

type TargetEntity = 'quote_request' | 'quote_request_bond';

type FieldValueRowForm = FormGroup<{
  field: FormControl<string | null>;
  value: FormControl<unknown>;
}>;

type OrderNumberIssueReason = 'invalid_format' | 'duplicate';
type OrderNumberIssue = { value: string; reason: OrderNumberIssueReason };

// Order number format (10 chars):
// - starts with 'w'
// - then one of [s v x y 2-9]
// - then 8 chars of [a-z 2-9] excluding i/o and 0/1
// This mirrors: /(w[svxy2-9]{1})([a-z0-9](?![0o1i])){5,10}/i but enforces full length 10.
const ORDER_NUMBER_REGEX = /^w[svxy2-9][a-hj-np-z2-9]{8}$/i;

@Component({
  selector: 'sbd-doug-frontend-policy-renewals-process-missing',
  templateUrl: './process-missing.component.html',
  styles: [
    `
      @use '../../../../libs/shared/src/styles/shared-styles.scss';

      :host {
        display: block;
        height: 100%;
        width: 100%;
        position: relative;
      }

      /* Smooth scrolling for the content area */
      .scrollable-content {
        -webkit-overflow-scrolling: touch; /* Momentum scrolling on iOS */
        overscroll-behavior: contain; /* Prevent scroll chaining */
        scroll-behavior: smooth;
        scrollbar-width: thin; /* Firefox thin scrollbar */
        scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

        /* Webkit scrollbar styling */
        &::-webkit-scrollbar {
          width: 6px;
        }

        &::-webkit-scrollbar-track {
          background: transparent;
        }

        &::-webkit-scrollbar-thumb {
          background: rgba(0, 0, 0, 0.15);
          border-radius: 3px;
        }

        &::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 0, 0, 0.3);
        }
      }
    `,
  ],
  imports: [
    PermissionsModule,
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    MaterialModule,
    LoadingStateComponent,
    CIPXActionBarComponent,
    CIPXPrimaryButtonComponent,
    CIPXCancelButtonComponent,
    CIPXButtonComponent,
    MatCheckboxModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatChipsModule,
  ],
})
export class ProcessMissingComponent implements OnInit {
  public readonly _titleService = inject(TitleService);
  public readonly processMissingService = inject(ProcessMissingService);
  public readonly _fb = inject(FormBuilder);
  public readonly _cathyService = inject(CathyService);
  protected readonly lastScheduleResponse = signal<ProcessMissingScheduleResponse | null>(null);
  protected readonly lastScheduleNetworkError = signal<unknown | null>(null);
  protected readonly lastSubmittedOrderNumbers = signal<string[]>([]);
  protected readonly lastScheduledOrderNumbers = signal<string[]>([]);
  protected readonly uiFieldsResponse = signal<ProcessMissingUiFieldsResponse | null>(null);
  protected readonly uiFieldsNetworkError = signal<unknown | null>(null);
  protected readonly form = this._fb.group({
    orderNumbers: this._fb.nonNullable.control('', [Validators.required, this._orderNumbersValidator()]),

    quoteInstantPrices: this._fb.nonNullable.control(false),
    copyBondNumber: this._fb.nonNullable.control(false),
    skipAssignments: this._fb.nonNullable.control(false),

    setQuoteRequestValues: this._fb.array<FieldValueRowForm>([]),
    setQuoteRequestBondValues: this._fb.array<FieldValueRowForm>([]),
  });

  protected readonly quoteRequestFields = computed(() => {
    const ui = this.uiFieldsResponse()?.data?.fields?.quote_request ?? null;
    return ui ? this._fieldMetaFromUiFields(ui) : [];
  });

  protected readonly quoteRequestBondFields = computed(() => {
    const ui = this.uiFieldsResponse()?.data?.fields?.quote_request_bond ?? null;
    return ui ? this._fieldMetaFromUiFields(ui) : [];
  });

  protected readonly isUiFieldsLoading = computed(() => !this.uiFieldsResponse() && !this.uiFieldsNetworkError());

  protected readonly isUiFieldsReady = computed(() => {
    const res = this.uiFieldsResponse();
    return !!(res && res.success && res.data?.fields);
  });

  constructor() {
    // Start with one blank row to make the intent obvious.
    this.addRow('quote_request');
    this.addRow('quote_request_bond');

    // Load field metadata for dynamic field/value inputs.
    this.processMissingService.getUiFields().subscribe({
      next: (res) => {
        this.uiFieldsResponse.set(res);
        this._setRowControlsDisabled(!(res.success && !!res.data?.fields));
      },
      error: (err) => {
        this.uiFieldsNetworkError.set(err);
        this._setRowControlsDisabled(true);
      },
    });
  }

  ngOnInit(): void {
    this._titleService.setTitle('Process Missing Renewals');
  }

  protected get quoteRequestRows(): FormArray<FieldValueRowForm> {
    return this.form.controls.setQuoteRequestValues;
  }

  protected get quoteRequestBondRows(): FormArray<FieldValueRowForm> {
    return this.form.controls.setQuoteRequestBondValues;
  }

  protected addRow(target: TargetEntity): void {
    const fieldsReady = this.isUiFieldsReady();
    const row = this._fb.group(
      {
        field: this._fb.control<string | null>({ value: null, disabled: !fieldsReady }),
        value: this._fb.control<unknown>({ value: null, disabled: !fieldsReady }),
      },
      { validators: [this._fieldValueRowValidator()] }
    );

    this._rowsFor(target).push(row);
  }

  protected removeRow(target: TargetEntity, idx: number): void {
    const rows = this._rowsFor(target);
    if (rows.length <= 1) {
      rows.at(0)?.reset();
      return;
    }
    rows.removeAt(idx);
  }

  protected onFieldChanged(target: TargetEntity, idx: number): void {
    // Reset value when field changes to avoid type mismatch / stale values.
    const row = this._rowsFor(target).at(idx);
    row?.controls.value.reset(null);
  }

  protected getFieldType(target: TargetEntity, fieldKey: string | null | undefined): FieldType | null {
    if (!fieldKey) return null;
    const list = target === 'quote_request' ? this.quoteRequestFields() : this.quoteRequestBondFields();
    return list.find((f) => f.key === fieldKey)?.type ?? null;
  }

  protected onCancel(): void {
    this._resetFormValues();
    this.lastScheduleResponse.set(null);
    this.lastScheduleNetworkError.set(null);
    this.lastSubmittedOrderNumbers.set([]);
    this.lastScheduledOrderNumbers.set([]);
  }

  protected onProcess(): void {
    const payload = this._buildRequestPayload();
    this.lastSubmittedOrderNumbers.set(payload.order_numbers);
    this.processMissingService.schedule(payload).subscribe({
      next: (res) => {
        this.lastScheduleResponse.set(res);
        this.lastScheduledOrderNumbers.set(res.data?.scheduled?.map((item) => item.order_number) ?? []);
        if(Array.isArray(res.data?.unable_to_process) && res.data?.unable_to_process.length > 0) {
          this._cathyService.error('Some order numbers were unable to be queued. Please review on screen log.');
        } else {
          this._cathyService.success('Successfully scheduled missing renewals to process.');
        }
        this._resetFormValues();
      },
      error: (err) => {
        this.lastScheduleNetworkError.set(err);
      },
    });
  }

  private _buildRequestPayload(): {
    order_numbers: string[];
    configuration: {
      instructions: {
        copy_bond_number: boolean;
        quote_instant_bonds: boolean;
        assignments?: {
          assign_to: string;
        };
      };
      actions: Array<{
        action_type: 'assign';
        values: Array<{
          value_type: 'fixed_value';
          object_token: TargetEntity;
          field: string;
          value: unknown;
        }>;
      }>;
    };
  } {
    const order_numbers = this._analyzeOrderNumbers(this.form.controls.orderNumbers.value).valid;
    const configuration = this._buildConfigurationPayload();

    return { order_numbers, configuration };
  }

  private _resetFormValues(): void {
    this.form.reset({
      orderNumbers: '',
      quoteInstantPrices: false,
      copyBondNumber: false,
      skipAssignments: false,
    });

    this.form.setControl('setQuoteRequestValues', this._fb.array<FieldValueRowForm>([]));
    this.form.setControl('setQuoteRequestBondValues', this._fb.array<FieldValueRowForm>([]));
    this.addRow('quote_request');
    this.addRow('quote_request_bond');
    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  private _setRowControlsDisabled(disabled: boolean): void {
    const toggle = (rows: FormArray<FieldValueRowForm>) => {
      for (const row of rows.controls) {
        if (disabled) {
          row.controls.field.disable({ emitEvent: false });
          row.controls.value.disable({ emitEvent: false });
        } else {
          row.controls.field.enable({ emitEvent: false });
          row.controls.value.enable({ emitEvent: false });
        }
      }
    };

    toggle(this.quoteRequestRows);
    toggle(this.quoteRequestBondRows);
  }

  private _buildConfigurationPayload(): {
    instructions: {
      copy_bond_number: boolean;
      quote_instant_bonds: boolean;
      assignments?: {
        assign_to: string;
      };
    };
    actions: Array<{
      action_type: 'assign';
      values: Array<{
        value_type: 'fixed_value';
        object_token: TargetEntity;
        field: string;
        value: unknown;
      }>;
    }>;
  } {
    const instructions: {
      copy_bond_number: boolean;
      quote_instant_bonds: boolean;
      assignments?: { assign_to: string };
    } = {
      copy_bond_number: this.form.controls.copyBondNumber.value,
      // NOTE: UI label says "Quote Instant Prices", but the API payload key requested is quote_instant_bonds.
      quote_instant_bonds: this.form.controls.quoteInstantPrices.value,
    };

    const assignTo = this.form.controls.skipAssignments.value
      ? PROCESS_MISSING_ASSIGN_TO.OPEN_ASSIGNMENT
      : PROCESS_MISSING_ASSIGN_TO.PREVIOUS_ASSIGNMENTS;

    if (assignTo) {
      instructions.assignments = { assign_to: assignTo };
    }

    const values = [
      ...this._rowsToActionValues('quote_request', this.quoteRequestRows),
      ...this._rowsToActionValues('quote_request_bond', this.quoteRequestBondRows),
    ];

    return {
      instructions,
      actions: [
        {
          action_type: 'assign',
          values,
        },
      ],
    };
  }

  protected analyzeOrderNumbers(raw: string): { valid: string[]; invalid: OrderNumberIssue[] } {
    return this._analyzeOrderNumbers(raw);
  }

  private _orderNumbersValidator(): ValidatorFn {
    return (control: AbstractControl<string>): ValidationErrors | null => {
      const { valid, invalid } = this._analyzeOrderNumbers(control.value ?? '');
      if (valid.length === 0) {
        return { orderNumbersEmpty: true };
      }
      if (invalid.length > 0) {
        return { invalidOrderNumbers: { invalid } };
      }
      return null;
    };
  }

  private _fieldValueRowValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const row = control as FieldValueRowForm;
      const field = row.controls.field.value;
      const value = row.controls.value.value;

      const hasField = typeof field === 'string' && field.trim().length > 0;

      const hasValue =
        value !== null &&
        value !== undefined &&
        !(typeof value === 'string' && value.trim().length === 0);

      // Empty row is allowed.
      if (!hasField && !hasValue) return null;

      // If either side is filled, require the other side too.
      if (hasField && !hasValue) return { rowValueRequired: true };
      if (!hasField && hasValue) return { rowFieldRequired: true };

      return null;
    };
  }

  private _analyzeOrderNumbers(raw: string): { valid: string[]; invalid: OrderNumberIssue[] } {
    const lines = (raw ?? '')
      .split(/\r?\n/g)
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    const valid: string[] = [];
    const invalid: OrderNumberIssue[] = [];
    const seen = new Set<string>();

    for (const v of lines) {
      if (!ORDER_NUMBER_REGEX.test(v)) {
        invalid.push({ value: v, reason: 'invalid_format' });
        continue;
      }
      if (seen.has(v)) {
        invalid.push({ value: v, reason: 'duplicate' });
        continue;
      }
      seen.add(v);
      valid.push(v);
    }

    return { valid, invalid };
  }

  private _rowsFor(target: TargetEntity): FormArray<FieldValueRowForm> {
    return target === 'quote_request' ? this.quoteRequestRows : this.quoteRequestBondRows;
  }

  private _normalizeRows(rows: FormArray<FieldValueRowForm>): Array<{ field: string; value: unknown }> {
    return rows.controls
      .map((row) => ({
        field: row.controls.field.value,
        value: row.controls.value.value,
      }))
      .filter((r): r is { field: string; value: unknown } => !!r.field);
  }

  private _fieldMetaFromUiFields(fields: Record<string, string>): FieldMeta[] {
    return Object.keys(fields)
      .sort((a, b) => a.localeCompare(b))
      .map((key) => {
        const type = this._coerceType(fields[key] ?? 'string');
        return { key, type, label: key };
      });
  }

  private _coerceType(typeRaw: string): FieldType {
    const t = typeRaw.trim().toLowerCase();
    if (
      t === 'integer' ||
      t === 'float' ||
      t === 'string' ||
      t === 'text' ||
      t === 'boolean' ||
      t === 'datetime' ||
      t === 'date' ||
      t === 'timestamp' ||
      t === 'json'
    ) {
      return t;
    }
    return 'string';
  }

  private _rowsToActionValues(
    target: TargetEntity,
    rows: FormArray<FieldValueRowForm>
  ): Array<{
    value_type: 'fixed_value';
    object_token: TargetEntity;
    field: string;
    value: unknown;
  }> {
    const list = target === 'quote_request' ? this.quoteRequestFields() : this.quoteRequestBondFields();

    return rows.controls
      .map((row) => {
        const field = row.controls.field.value;
        if (!field) return null;
        const type = list.find((f) => f.key === field)?.type ?? null;
        return {
          value_type: 'fixed_value' as const,
          object_token: target,
          field,
          value: this._coerceValue(type, row.controls.value.value),
        };
      })
      .filter(
        (v): v is { value_type: 'fixed_value'; object_token: TargetEntity; field: string; value: unknown } => v !== null
      );
  }

  private _coerceValue(type: FieldType | null, raw: unknown): unknown {
    if (raw === null || raw === undefined) return raw;

    switch (type) {
      case 'integer': {
        if (typeof raw === 'number') return Math.trunc(raw);
        if (typeof raw === 'string' && raw.trim() !== '') return Math.trunc(Number(raw));
        return raw;
      }
      case 'float': {
        if (typeof raw === 'number') return raw;
        if (typeof raw === 'string' && raw.trim() !== '') return Number(raw);
        return raw;
      }
      case 'boolean': {
        if (typeof raw === 'boolean') return raw;
        if (typeof raw === 'string') return ['true', '1', 'yes', 'y'].includes(raw.trim().toLowerCase());
        return Boolean(raw);
      }
      case 'json': {
        if (typeof raw !== 'string') return raw;
        const t = raw.trim();
        if (!t) return raw;
        try {
          return JSON.parse(t);
        } catch {
          return raw;
        }
      }
      default:
        return raw;
    }
  }
}
const PROCESS_MISSING_ASSIGN_TO = {
  OPEN_ASSIGNMENT: 'open_assignment',
  PREVIOUS_ASSIGNMENTS: 'previous_assignments',
} as const;
